/**
 * Employee Performance & Discipline Record (HR + admin only).
 * Pick an employee to see warnings (append-only, immutable audit) plus a read-only
 * roll-up of attendance, leave/LOP, manager feedback and missed daily updates.
 */
import { useState, useEffect, useCallback } from 'react';
import { disciplineAPI, employeeAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, ShieldAlert, AlertTriangle, CalendarDays, Plane, MessageSquare,
  ClipboardList, Plus, Ban, Gavel, CheckCircle2, CircleDashed, FileWarning, StickyNote,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'conduct', label: 'Conduct' },
  { value: 'non_compliance', label: 'Non-compliance / Email response' },
  { value: 'attendance', label: 'Attendance / Punctuality' },
  { value: 'performance', label: 'Performance' },
  { value: 'other', label: 'Other' },
];

const WARN_COLORS = { 1: 'bg-amber-100 text-amber-700', 2: 'bg-orange-100 text-orange-700', 3: 'bg-red-100 text-red-700' };

// Two visually distinct empty states required by HRM-001:
//  - CLEAN  = data source is tracked and there is nothing adverse to report (green)
//  - UNTRACKED = no data captured for this category yet (muted, dashed)
function CategoryTile({ icon: Icon, label, state, cleanText, untrackedText, children, testid }) {
  const isClean = state === 'clean';
  const isUntracked = state === 'untracked';
  return (
    <Card
      data-testid={testid}
      data-state={state}
      className={isUntracked ? 'border-dashed bg-muted/30' : ''}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
          <Icon className="w-4 h-4" /> {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {isUntracked ? (
          <p className="flex items-center gap-1.5 text-muted-foreground italic">
            <CircleDashed className="w-3.5 h-3.5" /> {untrackedText || 'Not captured yet'}
          </p>
        ) : isClean ? (
          <p className="flex items-center gap-1.5 text-green-600 font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" /> {cleanText || 'No adverse record'}
          </p>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}


export default function PerformanceDisciplinePage() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState('');
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ date: '', description: '', warning_number: '1', appraisal_impact: '', category: 'conduct' });
  const [saving, setSaving] = useState(false);

  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  useEffect(() => {
    employeeAPI.getAll({ limit: 1000 })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.employees || data.items || []);
        setEmployees(list.filter((e) => e.status === 'active' || !e.status));
      })
      .catch(() => toast.error('Failed to load employees'));
  }, []);

  const loadRecord = useCallback((empId) => {
    if (!empId) return;
    setLoading(true);
    disciplineAPI.getRecord(empId)
      .then(({ data }) => setRecord(data))
      .catch(() => toast.error('Failed to load record'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (selected) loadRecord(selected); }, [selected, loadRecord]);

  const openAdd = () => {
    const nextLevel = Math.min(3, (record?.warning_counts?.max_level || 0) + 1);
    setForm({ date: new Date().toISOString().slice(0, 10), description: '', warning_number: String(nextLevel), appraisal_impact: '', category: 'conduct' });
    setAddOpen(true);
  };

  const handleAdd = async () => {
    if (!form.description.trim() || !form.date) { toast.error('Date and description are required'); return; }
    setSaving(true);
    try {
      await disciplineAPI.createWarning({
        employee_id: selected,
        date: form.date,
        description: form.description.trim(),
        warning_number: parseInt(form.warning_number, 10),
        appraisal_impact: form.appraisal_impact.trim(),
        category: form.category,
      });
      toast.success('Warning recorded');
      setAddOpen(false);
      loadRecord(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record warning');
    } finally {
      setSaving(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) { toast.error('A correction reason is required'); return; }
    setVoiding(true);
    try {
      await disciplineAPI.voidWarning(voidTarget.id, voidReason.trim());
      toast.success('Warning voided (correction recorded)');
      setVoidTarget(null);
      setVoidReason('');
      loadRecord(selected);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to void');
    } finally {
      setVoiding(false);
    }
  };

  const agg = record?.aggregates;
  const activeWarnings = (record?.warnings || []).filter((w) => !w.voided);
  // Non-compliance = active warnings tagged non_compliance + the rolling missed-update rule breach.
  const nonComplianceCount = activeWarnings.filter((w) => w.category === 'non_compliance').length + (record?.warning_due ? 1 : 0);
  // Remarks = latest manager review comment (the only free-text HR note captured today).
  const remarkText = agg?.feedback?.latest?.manager_comment || '';

  return (
    <div className="space-y-6 p-1" data-testid="discipline-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Gavel className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Performance &amp; Discipline Record</h1>
          <p className="text-sm text-muted-foreground">Warnings and conduct history — HR &amp; admin only.</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <Label className="mb-2 block">Select employee</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-full md:w-96" data-testid="discipline-employee-select">
              <SelectValue placeholder="Choose an employee to view their record" />
            </SelectTrigger>
            <SelectContent>
              {employees.map((e) => (
                <SelectItem key={e.id} value={e.id}>{e.name} {e.designation ? `— ${e.designation}` : ''}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading && (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      )}

      {!loading && record && (
        <>
          {record.warning_due && (
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 bg-red-50 text-red-700" data-testid="discipline-warning-due">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <div className="text-sm">
                <strong>Warning due:</strong> {agg.daily_updates.missed} missed daily updates in the last {agg.daily_updates.window_days} days
                (threshold {agg.daily_updates.threshold}). Missed: {agg.daily_updates.missed_dates.join(', ') || '—'}
              </div>
            </div>
          )}

          {/* Read-only roll-up from existing data — HRM-001: every category is shown,
              with a clear distinction between "no adverse record" and "not captured yet". */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground" data-testid="discipline-legend">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> No adverse record</span>
            <span className="flex items-center gap-1.5"><CircleDashed className="w-3.5 h-3.5" /> Not captured yet</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 1. Attendance */}
            <CategoryTile
              icon={CalendarDays} label="Attendance (90d)" testid="discipline-attendance"
              state={agg.attendance.records === 0 ? 'untracked' : (agg.attendance.late === 0 && agg.attendance.absent === 0 ? 'clean' : 'data')}
              untrackedText="No attendance tracked" cleanText="Full attendance, no flags"
            >
              <p>{agg.attendance.present} present · <span className="text-orange-600">{agg.attendance.late} late</span> · <span className="text-red-600">{agg.attendance.absent} absent</span></p>
            </CategoryTile>

            {/* 2. Leaves / LOP */}
            <CategoryTile
              icon={Plane} label="Leave / LOP (this year)" testid="discipline-leave"
              state={agg.leave.approved_leave_days === 0 && agg.leave.lop_days === 0 ? 'clean' : 'data'}
              cleanText="No leave taken"
            >
              <p>{agg.leave.approved_leave_days} approved days · <span className="text-red-600">{agg.leave.lop_days} LOP</span></p>
            </CategoryTile>

            {/* 3. Daily updates */}
            <CategoryTile
              icon={ClipboardList} label="Daily updates (rolling 7d)" testid="discipline-daily"
              state={agg.daily_updates.working_days === 0 ? 'untracked' : (agg.daily_updates.missed === 0 ? 'clean' : 'data')}
              untrackedText="No working days in window" cleanText="All updates filed"
            >
              <p><span className="text-2xl font-bold">{agg.daily_updates.missed}</span> missed / {agg.daily_updates.working_days} working days</p>
            </CategoryTile>

            {/* 4. Manager feedback */}
            <CategoryTile
              icon={MessageSquare} label="Manager feedback" testid="discipline-feedback"
              state={agg.feedback.has_feedback ? 'data' : 'untracked'}
              untrackedText="No review captured yet"
            >
              <p>{agg.feedback.latest?.cycle}{agg.feedback.latest?.rating ? ` · ${agg.feedback.latest.rating}` : ''}</p>
            </CategoryTile>

            {/* 5. Non-compliance (derived: non_compliance warnings + missed-update rule) */}
            <CategoryTile
              icon={FileWarning} label="Non-compliance" testid="discipline-noncompliance"
              state={nonComplianceCount === 0 ? 'clean' : 'data'}
              cleanText="No non-compliance flags"
            >
              <p><span className="text-2xl font-bold text-red-600">{nonComplianceCount}</span> flag(s) {record.warning_due ? '· update rule breached' : ''}</p>
            </CategoryTile>

            {/* 6. Remarks (HR notes) */}
            <CategoryTile
              icon={StickyNote} label="Remarks" testid="discipline-remarks"
              state={remarkText ? 'data' : 'untracked'}
              untrackedText="No HR remarks captured yet"
            >
              <p className="line-clamp-3">{remarkText}</p>
            </CategoryTile>
          </div>
          {/* 7th category — Discipline / Warnings — is the detailed section below. */}

          {/* Warnings */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5" /> Warnings
                  <Badge variant="outline">{record.warning_counts.active} active</Badge>
                </CardTitle>
                <CardDescription>Appraisal-impacting record. Entries can't be edited or deleted — correct with a void + reason.</CardDescription>
              </div>
              <Button onClick={openAdd} data-testid="discipline-add-warning">
                <Plus className="w-4 h-4 mr-1" /> Add Warning
              </Button>
            </CardHeader>
            <CardContent>
              {record.warnings.length === 0 ? (
                <p className="text-center text-muted-foreground py-10" data-testid="discipline-no-warnings">No warnings on record.</p>
              ) : (
                <div className="space-y-3">
                  {record.warnings.map((w) => (
                    <div key={w.id} className={`p-4 rounded-lg border ${w.voided ? 'opacity-60 bg-muted/40' : ''}`} data-testid={`discipline-warning-${w.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={WARN_COLORS[w.warning_number] || ''}>Warning #{w.warning_number}</Badge>
                            <Badge variant="outline">{CATEGORIES.find((c) => c.value === w.category)?.label || w.category}</Badge>
                            <span className="text-xs text-muted-foreground">{w.date}</span>
                            {w.voided && <Badge variant="secondary">Voided</Badge>}
                          </div>
                          <p className="mt-2 text-sm">{w.description}</p>
                          {w.appraisal_impact && <p className="text-xs text-red-600 mt-1">Appraisal impact: {w.appraisal_impact}</p>}
                          <p className="text-xs text-muted-foreground mt-1">Issued by {w.issued_by_name}</p>
                          {w.voided && (
                            <p className="text-xs text-muted-foreground mt-1 italic">Voided by {w.voided_by_name}: {w.void_reason}</p>
                          )}
                        </div>
                        {!w.voided && (
                          <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => { setVoidTarget(w); setVoidReason(''); }} data-testid={`discipline-void-${w.id}`}>
                            <Ban className="w-4 h-4 mr-1" /> Void
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add warning dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="discipline-add-dialog">
          <DialogHeader>
            <DialogTitle>Record a Warning</DialogTitle>
            <DialogDescription>This becomes part of the employee's permanent, auditable record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} data-testid="discipline-form-date" />
              </div>
              <div>
                <Label>Warning number</Label>
                <Select value={form.warning_number} onValueChange={(v) => setForm((p) => ({ ...p, warning_number: v }))}>
                  <SelectTrigger data-testid="discipline-form-number"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1st Warning</SelectItem>
                    <SelectItem value="2">2nd Warning</SelectItem>
                    <SelectItem value="3">3rd Warning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="discipline-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>What happened</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe the incident / non-compliance" data-testid="discipline-form-description" />
            </div>
            <div>
              <Label>Appraisal impact</Label>
              <Input value={form.appraisal_impact} onChange={(e) => setForm((p) => ({ ...p, appraisal_impact: e.target.value }))}
                placeholder="e.g. -2 appraisal points (Nov-Dec 2026 cycle)" data-testid="discipline-form-impact" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={saving} data-testid="discipline-form-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Record Warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void dialog */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) setVoidTarget(null); }}>
        <DialogContent data-testid="discipline-void-dialog">
          <DialogHeader>
            <DialogTitle>Void Warning #{voidTarget?.warning_number}</DialogTitle>
            <DialogDescription>The warning stays on record as voided with your reason — nothing is deleted.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Reason for correction *</Label>
            <Textarea rows={3} value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              placeholder="Why is this warning being voided?" data-testid="discipline-void-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleVoid} disabled={voiding || !voidReason.trim()} data-testid="discipline-void-confirm">
              {voiding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Void Warning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
